/**
 * Entity names and paths. Names are display labels, as in Unreal and Unity:
 * they may repeat anywhere except among siblings, so an entity's path from
 * the level root (`Riot_Games/Zone_Staff_2025/Ahri`) is unique. Ids stay the
 * stable identity everything else runs on.
 *
 * A reference (in a level file, `level.open`'s `select`, a lookup) resolves by
 * id, then by full path, then by bare name only when exactly one entity has it.
 */
import type { EntityId } from "./entity";

/** What resolution needs of an entity: satisfied by EntityRecord and by manifest entries. */
export interface NamedEntity {
  readonly id: EntityId;
  readonly name: string;
  readonly parent: EntityId | null;
}

export type EntityLookup =
  | { readonly found: EntityId }
  | { readonly ambiguous: readonly string[] }
  | { readonly missing: true };

/** Separates path segments, so names can't contain it. */
export const PATH_SEPARATOR = "/";

/** Names from the root down, joined by `/`. */
export function entityPath(entities: ReadonlyMap<EntityId, NamedEntity>, id: EntityId): string {
  const names: string[] = [];
  for (
    let current = entities.get(id);
    current;
    current = current.parent === null ? undefined : entities.get(current.parent)
  ) {
    names.unshift(current.name);
  }
  return names.join(PATH_SEPARATOR);
}

export function findEntity(
  entities: ReadonlyMap<EntityId, NamedEntity>,
  reference: string,
): EntityLookup {
  if (entities.has(reference as EntityId)) return { found: reference as EntityId };

  const byPath = reference.includes(PATH_SEPARATOR);
  const matches = [...entities.values()].filter((entity) =>
    byPath ? entityPath(entities, entity.id) === reference : entity.name === reference,
  );

  const [only] = matches;
  if (only && matches.length === 1) return { found: only.id };
  if (matches.length === 0) return { missing: true };
  return { ambiguous: matches.map((entity) => entityPath(entities, entity.id)).sort() };
}

/** Why a name can't be used, or null when it can. */
export function invalidNameReason(name: string): string | null {
  if (name.trim() === "") return "Name cannot be empty";
  if (name.includes(PATH_SEPARATOR)) return "Names can't contain '/' (it separates path segments)";
  return null;
}
